import { ACTIVATION_PAGE_V1 } from '@app/code/src/constants/optimizely'
import { CreateCode, CreateFlowcodeReturnType } from '../CreateFlowcodeForm.types'
import { DEFAULT_ERROR, getGqlError, getGqlErrorMessage } from '@app/code/src/utils/errors'
import { DOWNLOAD_FLOWCODE_SUCCESS_MESSAGE } from '@app/code/src/constants/flowcode'
import { FLOWCODE_ROOT } from '@dtx-company/true-common/src/constants/root'
import { FcGeneratorOptions } from '@dtx-company/flowcode-generator-types/src'
import {
  FlowcodeTemplateType,
  LandingPageDestinationType,
  LandingPageFormValuesType
} from '@dtx-company/inter-app/src/types/flowcode'
import { FlowcodeThemeType } from '@app/common/src/types/createFlowcode.types'
import { GqlErrorCodes } from '@app/code/src/constants/gql-errors'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { createFlowcode, downloadFlowcode } from '../CreateFlowcodeForm.utils'
import { getCreatedCodeHeapProperties } from '../../../../utils/flowcodeHeapProperties'
import { getFlowcodeId, loadImage } from '../../../../utils/flowcode'
import { logger } from '@dtx-company/logger'
import {
  sendErrorNotification,
  sendSuccessNotification
} from '@dtx-company/inter-app/src/utils/notifications'
import { updateDownloadCodeData } from '@dtx-company/inter-app/src/redux/slices/homePageSlice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCallback } from 'react'
import { useCreateFlowcodeForm, useCreateFlowcodeStateActions } from '../CreateFlowcodeForm.hooks'
import { useCreateFlowcodeState } from '../../../../hooks/useCreateFlowcodeState'
import { useDispatch } from 'react-redux'
import { useExperiment } from '@optimizely/react-sdk'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowcode'

export type CreateCodeArguments = {
  centerImageObjectURL: string
  destinationFormValues: LandingPageFormValuesType
  options: FcGeneratorOptions
  selectedDestination: LandingPageDestinationType
  template: FlowcodeTemplateType | null
  theme: FlowcodeThemeType
  token: string | undefined
}

type CreateCodeAndDownloadAndRedirect = (
  onSuccess?: (batchId: string) => void,
  onError?: () => void
) => Promise<void>

// #1 store state fetch
export function useBuildCreateCodePayload(): CreateCodeArguments {
  const {
    destination: selectedDestination,
    options,
    template,
    theme,
    centerImage: { objectUrl: centerImageObjectURL }
  } = useCreateFlowcodeState()
  const { watch } = useCreateFlowcodeForm()
  const { token } = useAuthState()
  const destinationFormValues = watch()

  return {
    selectedDestination,
    options,
    template,
    theme,
    destinationFormValues,
    token,
    centerImageObjectURL
  }
}

// #2 The action that actually creates the code
export const createCode = async ({
  centerImageObjectURL,
  destinationFormValues,
  options,
  selectedDestination,
  template,
  theme,
  token
}: CreateCodeArguments): Promise<CreateFlowcodeReturnType> => {
  const centerImageFile = centerImageObjectURL
    ? await loadImage(centerImageObjectURL, 'centerImageFile')
    : null
  const { flowcodeOptions, flowcodeBatch } = await createFlowcode(
    token,
    {
      selectedDestination,
      flowcodeOptions: options,
      destinationFormValues
    },
    options,
    centerImageFile
  )
  const flowcodeId = getFlowcodeId(flowcodeBatch.shortUrl || '')
  const heapProperties = getCreatedCodeHeapProperties({
    isAutoDesign: false,
    selectedFlowcodeTemplate: template,
    flowcodeTheme: theme,
    centerImage: Boolean(centerImageFile),
    selectedDestination: selectedDestination.id,
    flowcodeOptions: options
  })
  events.userCreatedFlowcode(false, flowcodeId, heapProperties)

  return { flowcodeOptions, flowcodeBatch }
}

// #3 Binding hook between store values and code creation util
// Same as importing createCode and useBuildCreateCodePayload in place, left as-is for compatibility for co-dep modules
// Need to access createCode and fire via values from storage
export function useCreateCode(): CreateCode {
  const createCodeArguments = useBuildCreateCodePayload()

  return () => createCode(createCodeArguments)
}

// NOTE: this is a partial clone of the auto design functions that pulls values from the createflowcode - will deprecate or integrate the auto design logic depending on A/B test results. This hook also tracks some events that existed in the old flow

// #4 Calls create then updates download state
export function useCreateAndDownloadCode(): CreateCodeAndDownloadAndRedirect {
  const { downloadFileType } = useCreateFlowcodeState()
  const [variant] = useExperiment(ACTIVATION_PAGE_V1)
  const redirectToActivationPage = variant && variant !== 'control'
  const createCodeFromState = useCreateCode()
  const { setLoadingCreateFlowcode } = useCreateFlowcodeStateActions()
  const dispatch = useDispatch()

  const createCodeAndDownloadAndRedirect = useCallback<CreateCodeAndDownloadAndRedirect>(
    async (onSuccess, onError) => {
      try {
        // if createCodeArguments exist in localStorage use those instead of what's in state
        const { flowcodeOptions, flowcodeBatch } = await createCodeFromState()

        setLoadingCreateFlowcode(true)
        const flowcodeId = getFlowcodeId(flowcodeBatch.shortUrl)
        dispatch(updateDownloadCodeData({ codeOptions: flowcodeOptions, codeBatchId: flowcodeId }))

        await downloadFlowcode(downloadFileType, flowcodeOptions, flowcodeId)

        onSuccess
          ? onSuccess(flowcodeBatch.batchId)
          : sendSuccessNotification(DOWNLOAD_FLOWCODE_SUCCESS_MESSAGE)

        const redirectPath = redirectToActivationPage
          ? `${Routes.FLOWCODE_SUCCESS}?batchId=${flowcodeBatch.batchId}&v=${variant || 'control'}`
          : `${Routes.CODES}/${flowcodeBatch.batchId}`

        window.location.href = `${FLOWCODE_ROOT}${redirectPath}` //used on www and app subdomains, can't use router.push
      } catch (e) {
        logger.logError(e, { technicalArea: 'authentication' })
        const { code } = getGqlError(e, false)
        if (e.code === GqlErrorCodes.CODE_LIMIT_ERROR || code === GqlErrorCodes.CODE_LIMIT_ERROR) {
          onError?.()
        } else {
          // getGqlError was displaying a snackbar, moving that to here because
          // we don't want the snackbar in the case of a limit error that also opens
          // the upsell modal
          const message = getGqlErrorMessage(e, DEFAULT_ERROR.message)
          sendErrorNotification(message)
        }
      } finally {
        setLoadingCreateFlowcode(false)
      }
    },
    [
      createCodeFromState,
      setLoadingCreateFlowcode,
      dispatch,
      redirectToActivationPage,
      variant,
      downloadFileType
    ]
  )

  return createCodeAndDownloadAndRedirect
}
